import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = [
        "step", "indicator", "label", "progress",
        "nextButton", "backButton", "submitButton"
    ]
    static values = {
        plan: String
    }

    connect() {
        this.currentStep = 1
        this.trackEvent('contact_wizard_viewed', { plan: this.planValue })

        // Clear validation on input
        this.element.querySelectorAll('input, select, textarea').forEach(input => {
            input.addEventListener('input', () => {
                this.clearError(input)
            })
            // Clear radio group error on change
            if (input.type === 'radio') {
                input.addEventListener('change', () => {
                    this.clearError(input)
                })
            }
        })

        this.updateUI()
    }

    next(event) {
        event.preventDefault()
        if (this.validateStep(this.currentStep)) {
            this.trackEvent(`contact_wizard_step_${this.currentStep}_complete`)
            this.currentStep++
            this.updateUI()
        }
    }

    back(event) {
        event.preventDefault()
        if (this.currentStep > 1) {
            this.currentStep--
            this.updateUI()
        }
    }

    submit(event) {
        this.trackEvent('contact_wizard_submitted')
    }

    updateUI() {
        // Hide all steps, show current
        this.stepTargets.forEach((el, index) => {
            if (index + 1 === this.currentStep) {
                el.classList.remove('hidden')
            } else {
                el.classList.add('hidden')
            }
        })

        // Update Indicators
        this.indicatorTargets.forEach((el, index) => {
            const stepNum = index + 1
            const label = this.labelTargets[index]

            // Reset base state
            el.className = 'wizard-indicator'
            el.innerHTML = stepNum
            if (label) label.className = 'wizard-label'

            if (stepNum < this.currentStep) {
                // Completed
                el.classList.add('completed')
                el.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>'
            } else if (stepNum === this.currentStep) {
                // Current
                el.classList.add('active')
                if (label) label.classList.add('active')
            }
            // Future state is default (no extra classes)
        })

        // Update Connectors
        this.progressTargets.forEach((el, index) => {
            // index 0 connects step 1->2. If currentStep > 1, it is completed.
            if (this.currentStep > (index + 1)) {
                el.classList.add('completed')
            } else {
                el.classList.remove('completed')
            }
        })

        // Button Visibility
        if (this.currentStep === 1) {
            this.backButtonTarget.classList.add('hidden')
            this.nextButtonTarget.classList.remove('hidden')
            this.submitButtonTarget.classList.add('hidden')
        } else if (this.currentStep === 2) {
            this.backButtonTarget.classList.remove('hidden')
            this.nextButtonTarget.classList.remove('hidden')
            this.submitButtonTarget.classList.add('hidden')
        } else {
            this.backButtonTarget.classList.remove('hidden')
            this.nextButtonTarget.classList.add('hidden')
            this.submitButtonTarget.classList.remove('hidden')
        }
    }

    validateStep(step) {
        // Find the step element. stepTargets is array, index is step-1
        const stepEl = this.stepTargets[step - 1]
        const inputs = stepEl.querySelectorAll('input, select, textarea')
        let isValid = true

        // To prevent duplicate validation on radio groups, set track processed names
        const processedRadioGroups = new Set()

        inputs.forEach(input => {
            if (!input.hasAttribute('required')) return

            if (input.type === 'radio') {
                if (processedRadioGroups.has(input.name)) return
                processedRadioGroups.add(input.name)

                const group = stepEl.querySelectorAll(`input[name="${input.name}"]`)
                const isChecked = Array.from(group).some(radio => radio.checked)

                if (!isChecked) {
                    isValid = false
                    // Add error to the visual container (label > div)
                    group.forEach(radio => {
                        const visual = radio.nextElementSibling
                        if (visual) visual.classList.add('border-red-500', 'ring-1', 'ring-red-500')
                    })
                } else {
                    group.forEach(radio => {
                        const visual = radio.nextElementSibling
                        if (visual) visual.classList.remove('border-red-500', 'ring-1', 'ring-red-500')
                    })
                }
            } else {
                if (!input.value) {
                    isValid = false
                    input.classList.add('border-red-500', 'ring-1', 'ring-red-500', 'dark:border-red-500')
                } else {
                    this.clearError(input)
                }
            }
        })

        return isValid
    }

    clearError(input) {
        if (input.type === 'radio') {
            // Clear for whole group
            const name = input.name
            // We need to find all radios with this name in the form to clear styles
            // But really only the visible ones matter.
            const group = this.element.querySelectorAll(`input[name="${name}"]`)
            group.forEach(radio => {
                const visual = radio.nextElementSibling
                if (visual) visual.classList.remove('border-red-500', 'ring-1', 'ring-red-500')
            })
        } else {
            input.classList.remove('border-red-500', 'ring-1', 'ring-red-500', 'dark:border-red-500')
        }
    }

    trackEvent(eventName, props = {}) {
        console.log(`[Analytics] Tracking: ${eventName}`, props)
    }
}
